/**
 * GPX Mobile Configurator — cart builder.
 *
 * Fresh session (not editing an existing kit): just POST /cart/add.js with
 * items[] (each carrying _configurator_id = sessionId), then redirect to
 * /cart (or open the drawer if present). No prior lines to touch.
 *
 * Edit-mode rebuild is atomic: the new kit is added under a NEW session id
 * before the old kit's lines are removed, so a failed add always leaves the
 * customer's original kit intact — see handleBuild for the sequencing and
 * why the add has to happen before the remove (not the other way around).
 */

(function () {
  const CONFIG_PROP = '_configurator_id';
  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };

  function findRoot() {
    return document.querySelector('configurator-root');
  }

  // Shared fetch-and-parse for every cart endpoint: bounded linear-backoff
  // retry on 429/430 (rate limit / bot-check), parse-or-throw on the body.
  // A non-retryable status (422, etc.) or a non-JSON body (e.g. a Cloudflare
  // interstitial) throws immediately — retrying won't fix either of those.
  async function fetchJson(url, init, { label, retries = 0 } = {}) {
    for (let attempt = 0; attempt <= retries; attempt++) {
      const res = await fetch(url, init);
      if (res.ok) {
        const text = await res.text();
        try {
          return text ? JSON.parse(text) : {};
        } catch (e) {
          const snippet = text.slice(0, 80).replace(/\s+/g, ' ');
          throw new Error(`${label} returned non-JSON (status ${res.status}): ${snippet}`);
        }
      }
      if ((res.status === 429 || res.status === 430) && attempt < retries) {
        await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
        continue;
      }
      const text = await res.text();
      let json = null;
      try { json = text ? JSON.parse(text) : null; } catch (e) { /* non-JSON error body */ }
      if (json) throw new Error(json.description || json.message || `${label} ${res.status}`);
      const snippet = text.slice(0, 80).replace(/\s+/g, ' ');
      throw new Error(`${label} returned non-JSON (status ${res.status}): ${snippet}. Likely Cloudflare challenge.`);
    }
  }

  async function fetchCart(retries = 2) {
    return fetchJson('/cart.js', {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    }, { label: 'GET /cart.js', retries });
  }

  function configuratorLines(cart, sessionId) {
    return (cart.items || []).filter((item) => {
      const props = item.properties || {};
      return String(props[CONFIG_PROP] || '') === String(sessionId);
    });
  }

  function setError(root, msg) {
    const el = root.querySelector('[data-configurator-error]');
    if (!el) return;
    if (msg) { el.textContent = msg; el.hidden = false; }
    else { el.textContent = ''; el.hidden = true; }
  }

  function setBusy(root, busy) {
    const button = root.querySelector('[data-configurator-build]');
    const spinner = root.querySelector('.configurator__spinner');
    if (button) button.toggleAttribute('disabled', busy);
    if (spinner) spinner.classList.toggle('hidden', !busy);
  }

  async function addLines(items, sectionsToRender) {
    // Shopify's /cart/add.js stores a multi-item batch in the REVERSE of the
    // order it was sent (confirmed platform behavior, not a bug on our end).
    // Sending pre-reversed cancels that out, so cart.items ends up in actual
    // configured order — which cart-drawer.liquid and Shopify's own native
    // checkout summary both read raw, with no reverse of their own.
    const body = { items: [...items].reverse() };
    if (sectionsToRender && sectionsToRender.length) {
      body.sections = sectionsToRender.join(',');
      body.sections_url = window.location.pathname;
    }
    return fetchJson('/cart/add.js', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    }, { label: 'POST /cart/add.js', retries: 2 });
  }

  // Edit mode only: _buildCartLines() always stamps a fresh default
  // _device_name on newly-built lines (see configurator.js) — if the kit
  // being edited already had a real customer-given name, overwrite that
  // default on every new line so saving an edit doesn't silently rename the
  // kit back to "Unnamed Kit (...)". Sequential, not parallel — concurrent
  // /cart/change.js calls against the same cart lose writes.
  async function restampDeviceName(addedItems, oldKitName) {
    for (const item of addedItems) {
      const props = item.properties || {};
      const configuratorUrl = props._configurator_url || '';
      const isBaseLine = configuratorUrl && item.url && item.url.indexOf(configuratorUrl) === 0;
      const properties = Object.assign({}, props, { _device_name: oldKitName });
      if (isBaseLine) properties['Device Name'] = oldKitName;
      await fetchJson('/cart/change.js', {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ id: item.key, quantity: item.quantity, properties }),
      }, { label: 'POST /cart/change.js (device-name carryover)' });
    }
  }

  // Zeroes the old kit's lines and moves its saved name onto the new session
  // id, in one call. Idempotent (zeroing an already-zero line is a no-op),
  // so this is safe to retry on 429/430.
  async function removeOldLines(oldLineKeys, oldSessionId, newSessionId, oldKitName) {
    const updates = {};
    oldLineKeys.forEach((key) => { updates[key] = 0; });
    const attributes = {};
    attributes['kit_name_' + newSessionId] = oldKitName;
    attributes['kit_name_' + oldSessionId] = '';
    return fetchJson('/cart/update.js', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ updates, attributes }),
    }, { label: 'POST /cart/update.js', retries: 2 });
  }

  function getDrawerSections() {
    // shopify theme dev (127.0.0.1) trips Cloudflare bot-detection on
    // /cart/add.js when sections= is included. Skip in local dev only.
    if (window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost') {
      return [];
    }
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      return drawer.getSectionsToRender().map((s) => s.id);
    }
    return [];
  }

  function openCartDrawer(addResponse) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      if (typeof drawer.renderContents === 'function' && addResponse?.sections) {
        drawer.renderContents(addResponse);
        return;
      }
      if (typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
    }
    window.location.href = '/cart';
  }

  // Stable signature of the kit's cart lines — order-independent, so two
  // builds of the same selection compare equal.
  function cartSignature(state) {
    const lines = (state && state.cartLines) || [];
    return JSON.stringify(
      lines
        .map((l) => {
          const props = l.properties || {};
          return [
            Number(l.id),
            Number(l.quantity) || 0,
            Number(l.selling_plan) || 0,
            JSON.stringify(props, Object.keys(props).sort()),
          ];
        })
        .sort((a, b) => (a[0] - b[0]) || (a[2] - b[2]))
    );
  }

  // Edit mode only: flips the build button between "Back to cart" (no
  // changes — click is a no-op nav) and "Save changes" (rebuild needed).
  function updateEditLabel(root) {
    if (!root.isEditMode) return;
    const changed = cartSignature(root.getPublicState()) !== root._editPristine;
    root._editHasChanges = changed;
    const label = root.querySelector('[data-configurator-build-label]');
    if (label) label.textContent = changed ? 'Save changes' : 'Back to cart';
  }

  function generateSessionId(root) {
    return `${root.productId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  async function handleBuild(root, state) {
    if (!state.cartLines || state.cartLines.length === 0) {
      setError(root, 'Select at least one option to build a kit.');
      return;
    }
    // Edit mode with nothing changed — skip the rebuild, just go to the cart.
    if (root.isEditMode && root._editHasChanges === false) {
      window.location.href = '/cart';
      return;
    }
    setError(root, '');
    setBusy(root, true);

    const wasEditMode = root.isEditMode;
    const oldSessionId = state.sessionId;
    let cartLines = state.cartLines;
    let oldLineKeys = [];
    let oldKitName = '';

    try {
      if (wasEditMode) {
        // Capture what's currently in the cart under the OLD session id
        // before touching anything — this is what step 4 below removes.
        const cart = await fetchCart();
        oldLineKeys = configuratorLines(cart, oldSessionId).map((line) => line.key);
        oldKitName = String((cart.attributes || {})['kit_name_' + oldSessionId] || '');

        // Rebuild under a NEW session id so the add below can never merge
        // into the old lines (same options + same _configurator_id would
        // merge under Shopify's line-matching rules, and then zeroing "the
        // old keys" would delete the just-added lines too).
        const newSessionId = generateSessionId(root);
        root.sessionId = newSessionId;
        root._recompute();
        cartLines = root.getPublicState().cartLines;
      }

      const sections = getDrawerSections();
      let addRes;
      try {
        addRes = await addLines(cartLines, sections);
      } catch (err) {
        // Nothing has been removed yet — the customer's original kit is
        // still in the cart untouched. Just undo the session-id swap.
        if (wasEditMode) {
          root.sessionId = oldSessionId;
          root._recompute();
        }
        throw err;
      }

      if (wasEditMode && oldKitName && addRes && addRes.items) {
        try {
          await restampDeviceName(addRes.items, oldKitName);
        } catch (err) {
          // Best-effort: the rebuilt kit is still fully functional, it just
          // shows the default name instead of the customer's real one until
          // they re-save it — not worth failing the whole edit over.
          console.warn('[configurator] device-name carryover failed, kit added under a default name', err);
        }
      }

      if (wasEditMode && oldLineKeys.length) {
        try {
          await removeOldLines(oldLineKeys, oldSessionId, root.sessionId, oldKitName);
        } catch (err) {
          console.error('[configurator] old kit cleanup failed', err);
          setError(root, 'Your updated kit was added, but we couldn’t remove the previous version. Please delete the duplicate kit from your cart.');
          window.location.href = '/cart';
          return;
        }
      }

      openCartDrawer(addRes);
    } catch (err) {
      console.error('[configurator] build failed', err);
      setError(root, err.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(root, false);
    }
  }

  function init(root) {
    root.addEventListener('configurator:build', (e) => handleBuild(root, e.detail));
    maybeEnterEditMode(root);
  }

  // Edit mode: URL `?kit=<sessionId>` → adopt that session, hydrate from its cart lines.
  function maybeEnterEditMode(root) {
    const params = new URLSearchParams(window.location.search);
    const kitId = params.get('kit');
    if (!kitId) return;

    // Hide the preset chooser (Vehicle Type / Series cards) immediately —
    // we already know from the URL alone that this is an edit, so there's
    // no reason to wait on the fetchCart() round-trip below before hiding
    // it. Waiting on that network call was the flash: the chooser stayed
    // visible (its default state) for however long the fetch took, then
    // vanished once hydrate() → _enterChosenState() finally ran.
    if (root.chooserEl) root.chooserEl.hidden = true;
    root.classList.remove('configurator--awaiting-preset');

    fetchCart()
      .then((cart) => {
        const lines = configuratorLines(cart, kitId);
        if (lines.length === 0) {
          console.warn('[configurator] edit mode: no cart lines match kit id', kitId);
          return;
        }
        root.sessionId = kitId;
        root.isEditMode = true;
        const editHint = root.querySelector('[data-configurator-edit-hint]');
        if (editHint) editHint.hidden = false;
        const variantIndex = buildVariantIndex(root);
        let baseLineProps = {};
        const hydration = lines
          .map((line) => {
            const handle = variantIndex[String(line.variant_id)];
            const props = line.properties || {};
            // Base bundle line: capture its props for the panel (it carries
            // Activate Later / Attach Existing markers), don't hydrate as an option.
            if (String(line.variant_id) === String(root.baseBundleVariantId)) {
              baseLineProps = props;
              return null;
            }
            if (handle) {
              return { handle, qty: line.quantity, variantId: line.variant_id, properties: props };
            }
            // Unmapped (subscription tier / add-on) line — pass through for the panel.
            if (props._sub_panel || line.selling_plan_allocation || line.selling_plan) {
              return {
                id: line.variant_id,
                quantity: line.quantity,
                properties: props,
                selling_plan: (line.selling_plan_allocation && line.selling_plan_allocation.selling_plan
                  ? line.selling_plan_allocation.selling_plan.id : null),
              };
            }
            return null;
          })
          .filter(Boolean);
        root._baseLineProps = baseLineProps;
        root.hydrate(hydration, { mode: 'edit' });
        // Snapshot the hydrated kit, then track changes against it so the
        // build button reflects whether an update is actually needed.
        root._editPristine = cartSignature(root.getPublicState());
        updateEditLabel(root);
        root.addEventListener('configurator:statechange', () => updateEditLabel(root));
      })
      .catch((err) => console.warn('[configurator] edit mode hydration failed', err));
  }

  function buildVariantIndex(root) {
    const idx = {};
    root.querySelectorAll('configurator-card').forEach((card) => {
      const handle = card.dataset.handle;
      const def = card.dataset.defaultVariantId;
      if (def) idx[String(def)] = handle;
      // Both multi-variant UIs: radio pills ([data-configurator-variant]) and
      // the variant-mix qty rows ([data-variant-row], e.g. HDMI sizes).
      card.querySelectorAll('[data-configurator-variant], [data-variant-row]').forEach((el) => {
        idx[String(el.dataset.variantId)] = handle;
      });
    });
    return idx;
  }

  // Wait for root to upgrade then init.
  function ready() {
    const root = findRoot();
    if (!root) return;
    if (root.dataset.productId) {
      init(root);
    } else {
      root.addEventListener('configurator:ready', () => init(root), { once: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }
})();
